import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Avatar, Dropdown, Button, Space, Typography, message } from 'antd';
import { UserOutlined, SettingOutlined, LogoutOutlined, DashboardOutlined, HistoryOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getBaseURL } from '../config.js';

const { Header } = Layout;
const { Text } = Typography;

const TopNavBar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasToken, setHasToken] = useState(false);
    const baseURL = getBaseURL();

    // 检查token状态
    const checkTokenStatus = () => {
        const token = localStorage.getItem('token');
        setHasToken(!!token);
        return !!token;
    };

    const handleLogout = useCallback(() => {
        localStorage.removeItem('token');
        setUserInfo(null);
        setHasToken(false);
        message.success('Logged out successfully');
        navigate('/login');
    }, [navigate]);

    const fetchUserInfo = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setUserInfo(null);
                setHasToken(false);
                setLoading(false);
                return;
            }

            setHasToken(true);

            // 如果API不可用，使用token中的基本信息或默认信息
            try {
                const response = await axios.get(`http://localhost:8080/api/user/profile`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                
                // 根据新的API响应格式处理数据
                if (response.data.success) {
                    setUserInfo(response.data.data);
                } else {
                    throw new Error(response.data.message || 'Failed to fetch user info');
                }
            } catch (apiError) {
                console.log('API not available, using fallback user info');
                // API不可用时，创建一个基本的用户信息对象
                setUserInfo({
                    username: 'User', // 可以从token中解析或使用默认值
                    email: 'user@example.com',
                    avatar: null,
                    preferences: {
                        language: 'English',
                        depth: 'Medium'
                    }
                });
            }
        } catch (error) {
            console.error('Error in fetchUserInfo:', error);
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                // 其他错误，但有token的情况下仍显示基本用户信息
                if (hasToken) {
                    setUserInfo({
                        username: 'User',
                        email: 'user@example.com',
                        avatar: null
                    });
                }
            }
        } finally {
            setLoading(false);
        }
    }, [hasToken, handleLogout]);

    useEffect(() => {
        checkTokenStatus();
        fetchUserInfo();
    }, [location.pathname, fetchUserInfo]); // 监听路由变化

    const menuItems = [
        {
            key: 'dashboard',
            icon: <DashboardOutlined />,
            label: 'Dashboard',
            onClick: () => navigate('/dashboard')
        },
        {
            key: 'history',
            icon: <HistoryOutlined />,
            label: 'Learning History',
            onClick: () => navigate('/history')
        },
        {
            key: 'settings',
            icon: <SettingOutlined />,
            label: 'Settings',
            onClick: () => navigate('/settings')
        },
        {
            type: 'divider'
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Logout',
            onClick: handleLogout
        }
    ];

    // 不在登录和注册页面显示导航栏
    if (location.pathname === '/login' || location.pathname === '/register') {
        return null;
    }

    return (
        <Header style={{
            background: '#fff',
            padding: '0 24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1000
        }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Text strong style={{ fontSize: 18, color: '#1890ff', cursor: 'pointer' }}
                      onClick={() => navigate('/course-selection')}>
                    🌟 Galaxy Mentor
                </Text>
            </div>

            <Space>
                {!loading && hasToken && userInfo && (
                    <>
                        <Text type="secondary">Welcome, {userInfo.username}</Text>
                        <Dropdown
                            menu={{ items: menuItems }}
                            placement="bottomRight"
                            trigger={['click']}
                        >
                            <Button
                                type="text"
                                style={{
                                    height: 40,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8
                                }}
                            >
                                <Avatar
                                    size={32}
                                    src={userInfo.avatar}
                                    icon={<UserOutlined />}
                                    style={{ backgroundColor: '#1890ff' }}
                                />
                                <Text>{userInfo.username}</Text>
                            </Button>
                        </Dropdown>
                    </>
                )}
                
                {!loading && !hasToken && (
                    <Button
                        type="primary"
                        onClick={() => navigate('/login')}
                    >
                        Login
                    </Button>
                )}

                {loading && hasToken && (
                    <Text type="secondary">Loading...</Text>
                )}
            </Space>
        </Header>
    );
};

export default TopNavBar; 